/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

// hooks
import { useHistory } from 'react-router-dom';

// components
import Button, { IconButton } from '@atlaskit/button/new';
import DynamicTable from '@atlaskit/dynamic-table';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import RunIcon from '@atlaskit/icon/glyph/editor/outdent';
import Select from '@atlaskit/select';
import PageHeader from '@atlaskit/page-header';
import MoreIcon from '@atlaskit/icon/glyph/editor/more';
import HomeIcon from '@atlaskit/icon/glyph/home';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import Tooltip from '@atlaskit/tooltip';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import Textfield from '@atlaskit/textfield';
import { N100 } from '@atlaskit/theme/colors';
import IssuePanel from '../components/IssuePanel';
import If from '../components/If';
import { createIssueHead, TestBadge, Text } from '../service/tableService';
import useQuery from '../hooks/useQuery';
import { useStore } from '../store';
import useModel from '../hooks/useModel';

import logo from '../logo.png';
import Login from './Login';

const Summary = observer(({ testomatioURL }) => {
  const { noProject } = useModel('project');
  const store = useStore();
  const { jira, user } = store;
  const query = useQuery();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [issueTypes, setIssueTypes] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(0);
  const [issueTypeFilter, setIssueTypeFilter] = useState({ label: 'All types', value: '' });
  const [sprintID, setSprintID] = useState('');
  const [jql, setJql] = useState('');
  const [currentProject, setCurrentProject] = useState(null);
  const [totalIssues, setTotalIssues] = useState('...');
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenHistory, setTokenHistory] = useState([null]);
  const [currentStartAt, setCurrentStartAt] = useState(0);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setNextPageToken(null);
    setTokenHistory([null]);
    setCurrentStartAt(0);
    setHasPrev(false);
    setHasNext(false);
  }, []);

  const loadFilteredIssuesPage = useCallback(async (direction = 'first') => {
    if (!jira.isUserAuthorized()) return;
    setLoading(true);
    setTotalIssues('...');

    let params = {};
    if (direction === 'first') {
      resetPagination();
    } else if (direction === 'forward') {
      setCurrentPage((p) => p + 1);
      if (jira.searchApiMode === 'cloud') {
        params = { nextPageToken };
      } else {
        const newStartAt = currentStartAt + 100;
        setCurrentStartAt(newStartAt);
        params = { startAt: newStartAt };
      }
    } else if (direction === 'prev') {
      setCurrentPage((p) => Math.max(1, p - 1));
      if (jira.searchApiMode === 'cloud') {
        const newHistory = [...tokenHistory];
        newHistory.pop();
        setTokenHistory(newHistory);
        const prevToken = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;
        params = { nextPageToken: prevToken };
      } else {
        const newStartAt = Math.max(0, currentStartAt - 100);
        setCurrentStartAt(newStartAt);
        params = { startAt: newStartAt };
      }
    }

    jira.setBranch(null);
    const filter = { project: query.get('projectKey'), jql, sprint: sprintID, issuetype: issueTypeFilter?.id };
    const foundData = await jira.getIssuesWithTests(filter, params);
    const meta = jira.paginationMetaFromJiraApiPages(foundData);

    if (direction === 'first') {
      setCurrentPage(1);
      setTokenHistory([null]);
      setCurrentStartAt(0);
      setHasPrev(false);
    } else if (direction === 'prev') {
      setHasPrev(currentPage > 2);
    } else if (direction === 'forward') {
      setHasPrev(true);
    }

    if (jira.searchApiMode === 'cloud' && meta.nextPageToken) {
      setNextPageToken(meta.nextPageToken);
      if (direction === 'first') {
        setTokenHistory([null, meta.nextPageToken]);
      } else {
        setTokenHistory((prev) => [...prev, meta.nextPageToken]);
      }
    }

    setHasNext(meta.hasNext);
    setTotalIssues(meta.total);
    setFilteredIssues(foundData.issues || []);
    setLoading(false);
  }, [jira, query, issueTypeFilter, jql, sprintID, nextPageToken, tokenHistory, currentStartAt, currentPage, resetPagination]);

  useEffect(() => {
    loadFilteredIssuesPage('first');
    // eslint-disable-next-line
  }, [issueTypeFilter, currentProject, sprintID, jql]);

  useEffect(() => {
    loadProjectIssuesTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && noProject) return;
    if (!jira.testomatioProject) return;
    if (currentProject) return;
    setCurrentProject(jira.testomatioProject);
    // eslint-disable-next-line
  }, [jira.testomatioProject]);

  const showIssueHandle = async (id) => {
    jira.setCurrentIssue(id);
    setSelectedIssue(id);
  };

  const loadProjectIssuesTypes = async () => {
    if (issueTypes && (issueTypes.length > 1)) return;

    await jira.getIssueTypes(query.get('projectKey')).then((types) => {
      setIssueTypes(
        [{ label: 'All types', value: '' }, ...types.map((is) => ({ ...is, label: is.name, value: is.id }))],
      );
    });
  };

  const close = () => {
    jira.setCurrentIssue(null);
    setSelectedIssue(0);
  };

  const clearFilters = useCallback(async () => {
    setLoading(true);
    setIssueTypeFilter(null);
    setSprintID('');
    setCurrentProject(jira.testomatioProject);
    setJql('');
    jira.showAllProjects = false;
    resetPagination();
    setLoading(false);
  }, [jira, resetPagination]);

  const createIssueTableRows = (issueDetailArray, showMoreHandle) => {
    const automationPercent = (tests) => {
      if (!tests.length) return '0 %';
      const automatedCount = tests.reduce((acc, current) => {
        if (current.type === 'automated') return acc + 1;
        return acc;
      }, 0);
      return `${Math.round((automatedCount / tests.length) * 100)} %`;
    };

    const status = (tests) => {
      if (!tests.length) return '';
      const hasFailedStatus = tests.some(test => test.status[0] === 'failed');
      if (hasFailedStatus) return 'FAILED';

      const hasPassedStatus = tests.some(test => test.status[0] === 'passed');
      if (hasPassedStatus) return 'PASSED';

      return '';
    };

    return issueDetailArray.map((testDetail) => {
      return {
        key: `row-${testDetail.id}`,
        cells: [
          {
            key: 'title',
            content: (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <If condition={testDetail.fields?.issuetype}>
                    <img
                      src={testDetail.fields?.issuetype.iconUrl}
                      alt={testDetail.fields?.issuetype.name || ''}
                      className="w-4 h-4 mr-2"
                    />
                  </If>
                  <button
                    type="button"
                    className="text-blue-600 text-sm focus:outline-none"
                    onClick={() => jira.getNavigateToIssuePage(testDetail.key)}
                  >
                    <b className="font-bold pr-4">{testDetail.key}</b>
                    {' '}
                    {testDetail.fields?.summary || ''}
                  </button>
                </div>
                <span className="text-xs text-gray-700">
                  {testDetail.issueProjectsCount}
                  {' '}
                  projects
                </span>
              </div>
            ),
          },
          {
            key: `${testDetail.tests?.length || 0}`,
            content: (
              <button
                type="button"
                className="text-blue-600 focus:outline-none"
                onClick={() => showMoreHandle(testDetail.id, testDetail.key)}
              >
                {testDetail.tests?.length || 0}
                {' '}
                tests
              </button>
            ),
          },
          {
            key: 'status',
            content: <TestBadge type={status(testDetail.tests)} />,
          },
          {
            key: 'autoStatus',
            content: <Text data={automationPercent(testDetail.tests)} />,
          },
        ],
      };
    });
  };

  const getIssuesByJQL = (e) => {
    if (e.key === 'Enter') setJql(e.target.value);
  };

  const EmptyView = () => {
    return <div>No issues found</div>;
  };

  return (
    <div className="relative px-8">
      {!user.isLoggedIn && (
        <div className="max-w-screen-md w-full mt-4 px-4 py-24">
          <Login testomatioURL={testomatioURL} />
        </div>
      )}
      {user.isLoggedIn && user.currentUser && (
      <div className="page_content block w-full">
      <div className="flex justify-center flex-col items-center mt-2">
        <div className="flex text-sm justify-between bg-gray-100 rounded space-x-8 py-2 w-full items-center px-12">
          <div className="flex space-x-4 w-full justify-between">
            <div className="flex space-x-4 items-center">
              <a
                href={testomatioURL || '#'}
                rel="noopener noreferrer"
                className="cursor-pointer"
                target="_blank"
              >
                <img src={logo} alt="Testomatio" className="h-6 m-2" />
              </a>
              <a
                href="https://docs.testomat.io/integration/jira/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Docs
              </a>
            </div>
            <div className="flex space-x-4 items-center">
              <img
                alt=""
                src={user.currentUser.avatar}
                width={48}
                height={48}
                className="align-middle rounded-full ml-2"
              />
              <b>{user.currentUser.name}</b>
              <button
                onClick={() => user.userLogout(history)}
                type="submit"
                rel="noopener noreferrer"
                href="#"
                className="border-none text-sm text-blue-600"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center mt-1">
        <div className="flex-1">
          <PageHeader>Tests Coverage</PageHeader>
        </div>
        <div>
          <DropdownMenu
            trigger={({ triggerRef, ...props }) => {
              return <IconButton icon={MoreIcon} label="more" ref={triggerRef} {...props} />;
            }}
            position="bottom right"
          >
            <DropdownItemGroup>
              <DropdownItem
                key="home"
                elemBefore={<HomeIcon size="medium" />}
                description="List all projects"
                onClick={() => history.push('/home')}
              >
                Projects
              </DropdownItem>
              <DropdownItem
                key="sprints"
                elemBefore={<HomeIcon size="medium" />}
                description="List all sprints"
                onClick={() => history.push('/sprints')}
              >
                Sprints
              </DropdownItem>

              {jira.isGherkin && (
                <DropdownItem
                  key="edit-feature"
                  elemBefore={<RunIcon size="medium" />}
                  description="Update feature files in a project"
                  onClick={() => history.push('/edit_feature')}
                >
                  Edit Feature
                </DropdownItem>
              )}
            </DropdownItemGroup>
          </DropdownMenu>
        </div>
      </div>
      <div className="flex items-center space-x-2 w-full">
        <span>
          Projects:
        </span>

        <If condition={currentProject}>
          <DropdownMenu
            trigger={currentProject ? currentProject.title : ''}
            triggerType="button"
          >
            <DropdownItem
              isSelected={currentProject?.slug === '<=ALL=>'}
              onClick={() => {
                jira.showAllProjects = true;
                setSprintID('');
                jira.setBranch(null);
                setCurrentProject({ slug: '<=ALL=>', title: 'All Projects' });
              }}
              key="<=ALL=>"
            >
              All Projects
            </DropdownItem>
            {jira.projects && jira.projects.map((p) => (
              <DropdownItem
                isSelected={currentProject?.slug === p.slug}
                onClick={() => {
                  jira.showAllProjects = false;
                  setSprintID('');
                  jira.setBranch(null);
                  jira.setTestomatioProject(p);
                  setCurrentProject(p);
                }}
                key={p.slug}
              >
                {p.title}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </If>
        <If condition={!currentProject}>
          <span className="text-red-500 flex-none">&nbsp;No projects linked</span>
        </If>
        <span>
          Type:
        </span>

        <Select
          className="w-1/5 ml-4"
          classNamePrefix="react-select"
          options={issueTypes}
          value={issueTypeFilter}
          placeholder="Issue type"
          onChange={(item) => setIssueTypeFilter(item)}
        />
        <span>Sprint:</span>

        <div className="w-1/5">
          <Textfield
            placeholder="Enter Sprint ID"
            value={sprintID}
            onChange={(e) => setSprintID(e.target.value)}
            type="number"
            min="1"
          />
        </div>
        <span>
          JQL(
          <a
            href="https://support.atlassian.com/jira-service-management-cloud/docs/use-advanced-search-with-jira-query-language-jql/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm border-dashed border-b border-blue-500"
            style={{ 'white-space': 'nowrap' }}
          >
            Learn more
          </a>
          ):

        </span>
        <div className="w-1/5">
          <Textfield
            placeholder="Enter JQL request"
            value={jql}
            onChange={(e) => setJql(e.target.value)}
            type="text"
            onKeyPress={(e) => getIssuesByJQL(e)}
          />
        </div>

        <Button appearance="link" onClick={clearFilters}>
          clear
        </Button>

      </div>
      <div className="w-full">
        Found {totalIssues} issues
      </div>
      <DynamicTable
        emptyView={<EmptyView />}
        head={createIssueHead()}
        rows={createIssueTableRows(filteredIssues, showIssueHandle)}
        rowsPerPage={10000}
        defaultPage={1}
        loadingSpinnerSize="large"
        isLoading={loading}
        isFixedSize
        defaultSortOrder="ASC"
      />
      <div className="flex items-center justify-between my-2">
        <Button
          appearance="subtle"
          isDisabled={!hasPrev || loading}
          onClick={() => loadFilteredIssuesPage('prev')}
        >
          &larr; Prev
        </Button>
        <span className="text-sm text-gray-600">Page {currentPage}</span>
        <Button
          appearance="subtle"
          isDisabled={!hasNext || loading}
          onClick={() => loadFilteredIssuesPage('forward')}
        >
          Forward &rarr;
        </Button>
      </div>
      <ModalTransition>
        <If condition={selectedIssue !== 0}>
          <Modal
            actions={[{ text: 'Close', onClick: close }]}
            onClose={close}
            heading="Associated tests"
            scrollBehavior="inside"
            height={600}
            width={800}
            autoFocus={false}
          >
            <IssuePanel />
          </Modal>
        </If>
      </ModalTransition>
      <div className="absolute bottom-0 right-0 pr-2 flex items-start space-x-1">
        <Tooltip content="Use JQL for reduction search results">
          <QuestionCircleIcon size="small" primaryColor={N100} />
        </Tooltip>
      </div>
      </div>
      )}
    </div>
  );
});

export default Summary;
